<template>
    <ui-card class="pd-details-summary">
        <div class="detail-grid">
            <div class="detail">
                <div class="detail-label">District</div>
                <div class="detail-value" :class="{'is-empty': !salary.district_name}">{{ salary.district_name || 'Not set' }}</div>
            </div>
            <div class="detail">
                <div class="detail-label">School</div>
                <div class="detail-value" :class="{'is-empty': !school}">{{ school || 'Not set' }}</div>
            </div>
            <div class="detail">
                <div class="detail-label">Years teaching</div>
                <div class="detail-value" :class="{'is-empty': !salary.years_teaching}">{{ salary.years_teaching || 'Not set' }}</div>
            </div>
            <div class="detail">
                <div class="detail-label">Highest degree</div>
                <div class="detail-value" :class="{'is-empty': !salary.highest_degree}">{{ salary.highest_degree || 'Not set' }}</div>
            </div>
            <div class="detail">
                <div class="detail-label">Clock hour column</div>
                <div class="detail-value" :class="{'is-empty': !columnLabel}">{{ columnLabel || 'Not set' }}</div>
            </div>
            <div class="detail">
                <div class="detail-label">OSPI certificate number</div>
                <div class="detail-value" :class="{'is-empty': !ospiNumber}">{{ ospiNumber || 'Not set' }}<i v-if="ospiLocked" class="fas fa-lock ml-2 has-text-grey-light" title="Confirmed by an integration"></i></div>
            </div>
            <div class="detail">
                <div class="detail-label">Certificate renewal</div>
                <div class="detail-value" :class="{'is-empty': !profile.declared}">
                    <template v-if="profile.declared">{{ profile.renewal_on | dateOnly }} &middot; {{ profile.target_hours }} hours<template v-if="profile.external_hours"> &middot; {{ profile.external_hours | hours }} on file elsewhere</template></template>
                    <template v-else>Not declared</template>
                </div>
            </div>
            <div class="detail">
                <div class="detail-label">Work schedule</div>
                <div class="detail-value" :class="{'is-empty': !scheduleSummary}">
                    <template v-if="scheduleLoading"><i class="fas fa-spinner fa-spin"></i></template>
                    <template v-else>{{ scheduleSummary || 'Not declared' }}</template>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="summary-footer">
                <div class="is-size-7 has-text-grey">These drive your salary placement, renewal progress and study-time review.</div>
                <b-button type="is-primary" size="is-small" tag="router-link" :to="settingsRoute"><i class="fas fa-sliders mr-2"></i>Edit Settings</b-button>
            </div>
        </template>
    </ui-card>
</template>

<script>
    import {mapGetters} from "vuex";
    import {axiosInstance as http} from "@/http_client";
    import UiCard from "../UiCard";
    import salaryLane from "./salaryLane";
    import {CLOCK_HOURS} from "../../store/modules/certifications";
    import {scaleColumnOptions} from "../package/scaleMath";
    import {summarizeSchedule, fmtContractYear} from "../studyActivity/scheduleUtil";
    import {formatHours, pdRoute} from "@/utils/pd";

    /**
     * Read-only view of the professional-development settings on the
     * Overview (Derek 2026-09-16: settings on their own screen, but show
     * what they're set at here). Career profile + renewal from the PD
     * summary, OSPI number from the certifications store, work schedule
     * from the study-activity schedule endpoint.
     */
    export default {
        name: "PdDetailsSummary",
        components: {UiCard},
        mixins: [salaryLane],
        filters: {hours: formatHours},
        data() {
            return {schedule: null, scheduleLoading: true};
        },
        computed: {
            ...mapGetters(['currentUser', 'certificationById']),
            profile() {
                return (this.pdSummary && this.pdSummary.profile) || {declared: false};
            },
            school() {
                const s = this.currentUser && this.currentUser.school;
                return s && s !== 'Not Listed/Not Applicable' ? s : null;
            },
            // The district's own lane name when the column maps onto its
            // published schedule, else the stored '+N' credits
            columnLabel() {
                const v = this.salary.hours_column;
                if (!v) return null;
                if (v === 'This does not apply') return 'Does not apply';
                const options = scaleColumnOptions(this.scale || null, this.salary.highest_degree);
                const match = options && options.find(o => o.value === v);
                return match ? match.label : (this.salary.highest_degree || '') + v;
            },
            ospiCert() {
                return this.certificationById(CLOCK_HOURS);
            },
            ospiNumber() {
                return (this.ospiCert && this.ospiCert.certification_number) || null;
            },
            ospiLocked() {
                return !!(this.ospiCert && this.ospiCert.locked_at);
            },
            scheduleSummary() {
                if (!this.schedule || !this.schedule.declared) return null;
                const year = fmtContractYear(this.schedule.year_start, this.schedule.year_end);
                return summarizeSchedule(this.schedule.blocks) + (year ? ' · ' + year : '');
            },
            settingsRoute() {
                return pdRoute('settings');
            }
        },
        async mounted() {
            try {
                const {data} = await http.get('app/study-activity/schedule');
                this.schedule = data;
            } catch (e) {
                this.schedule = null;
            }
            this.scheduleLoading = false;
        }
    };
</script>

<style lang="scss" scoped>
    .detail-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 0.9rem 1.5rem;
    }

    .detail-label {
        font-size: 0.7rem;
        font-weight: 800;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: #7a8a9d;
    }

    .detail-value {
        font-weight: 600;
        color: #33475b;
        margin-top: 0.15rem;

        &.is-empty {
            font-weight: 400;
            color: #a0a9b4;
        }
    }

    .summary-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
    }
</style>
